import { useEffect } from 'react';
import IconClose from '@creditflowresearch/uilib/Icon/IconClose';

import styles from './popoverTip.module.scss';
import IconButton from '@creditflowresearch/uilib/IconButton';

const AUTO_CLOSE_TIMEOUT_MS = 8000;

interface IProps {
  dataTest: string,
  closeDataTest: string,
  title: React.ReactNode,
  onClose: () => void,
  closeAriaLabel?: string,
  /** Calls onClose on its own after AUTO_CLOSE_TIMEOUT_MS. Default false — requires manual close. */
  autoClose?: boolean,
  children: React.ReactNode,
}

/**
 * Shared shell for the small anchored cards (title + body + close button) used across
 * the app for one-off tips and notifications. Must be rendered inside a
 * `position: relative` ancestor — it positions itself absolutely under that ancestor's
 * bottom-right corner.
 */
const PopoverTip = ({
  dataTest,
  closeDataTest,
  title,
  onClose,
  closeAriaLabel = 'Close',
  autoClose = false,
  children,
}: IProps): React.ReactElement => {
  useEffect(() => {
    if (!autoClose) return undefined;

    const timeoutId = setTimeout(onClose, AUTO_CLOSE_TIMEOUT_MS);

    return () => clearTimeout(timeoutId);
  }, [autoClose, onClose]);

  return (
    <div className={ styles.container } role="status" aria-live="polite" data-test={ dataTest }>
      <div className={ styles.content }>
        <IconButton
          icon={ <IconClose className={ styles.closeIcon } /> }
          className={ styles.closeButton }
          onClick={ (e) => {
            e.stopPropagation();
            onClose();
          } }
          ariaLabel={ closeAriaLabel }
          dataTest={ closeDataTest }
        />
        <h3 className={ styles.title }>{ title }</h3>
        <div className={ styles.body }>{ children }</div>
      </div>
    </div>
  );
};

export default PopoverTip;
